import type { UserRole } from '@/api';
import type { InvokeResult, ReduxSagaModel } from './types';
import Workspace from '@/services/workspace';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { handleUserError } from '@/utils/handleError';
import { logger } from '@/utils/logger';
import {
  assertIsDefined,
  assertIsString,
  assertIsUser,
  assertIsWorkspace,
} from '@/utils/typeChecks';
import User from '@/services/user';

const ns = 'app.models.workspaces';

export interface State {
  current: Workspace | null;
  invokeCreateResult: InvokeResult<string> | null;
  invokeRenameResult: InvokeResult<null> | null;
  invokeAddMemberResult: InvokeResult<null> | null;
  invokeRemoveMemberResult: InvokeResult<null> | null;
  invokeUpdateMemberResult: InvokeResult<null> | null;
  invokeSetHomeDashboardResult: InvokeResult<null> | null;
  invokeCustomizeResult: InvokeResult<null> | null;
}

const initialState = {
  current: null,
  invokeCreateResult: null,
  invokeRenameResult: null,
  invokeAddMemberResult: null,
  invokeRemoveMemberResult: null,
  invokeUpdateMemberResult: null,
  invokeSetHomeDashboardResult: null,
  invokeCustomizeResult: null,
};

export type SaveCurrentAction = {
  type: 'saveCurrent' | 'workspaces/saveCurrent';
  payload: Pick<State, 'current'>;
};

interface SaveInvokeCreateResultAction {
  type: 'saveInvokeCreateResult';
  payload: Pick<State, 'invokeCreateResult'>;
}

interface SaveInvokeCustomizeResultAction {
  type: 'saveInvokeCustomizeResult';
  payload: Pick<State, 'invokeCustomizeResult'>;
}

/** Create workspace: the name and, optionally, the avatar, in one `createWorkspace` call. */
export type InvokeCreateEffect = {
  type: 'workspaces/invokeCreate';
  payload: {
    name: string;
    avatarColor?: string | null;
    avatarIcon?: string | null;
    avatarEmoji?: string | null;
    avatarInitials?: string | null;
  };
};

export type ResetInvokeCreateResultAction = {
  type: 'workspaces/saveInvokeCreateResult';
  payload: { invokeCreateResult: null };
};

/** The sidebar header's Customize dialog: the workspace's name and avatar, saved together. */
export type InvokeCustomizeEffect = {
  type: 'workspaces/invokeCustomize';
  payload: {
    record: Workspace;
    name: string;
    avatarColor: string | null;
    avatarIcon: string | null;
    avatarEmoji: string | null;
    avatarInitials: string | null;
  };
};

interface SaveInvokeRenameResultAction {
  type: 'saveInvokeRenameResult';
  payload: Pick<State, 'invokeRenameResult'>;
}

interface SaveInvokeAddMemberResultAction {
  type: 'saveInvokeAddMemberResult';
  payload: Pick<State, 'invokeAddMemberResult'>;
}

interface SaveInvokeRemoveMemberResultAction {
  type: 'saveInvokeRemoveMemberResult';
  payload: Pick<State, 'invokeRemoveMemberResult'>;
}

type SaveInvokeUpdateMemberResultAction = {
  type: 'saveInvokeUpdateMemberResult';
  payload: Pick<State, 'invokeUpdateMemberResult'>;
};

type SaveInvokeSetHomeDashboardResultAction = {
  type: 'saveInvokeSetHomeDashboardResult' | 'workspaces/saveInvokeSetHomeDashboardResult';
  payload: Pick<State, 'invokeSetHomeDashboardResult'>;
};

/** `dashboardId: null` clears Home, which puts the workspace back on the empty state. */
export type InvokeSetHomeDashboardEffect = {
  type: 'workspaces/invokeSetHomeDashboard';
  payload: { dashboardId: string | null };
};

export type InvokeUpdateMemberEffect = {
  type: 'workspaces/invokeUpdateMember';
  payload: { id: string; role: UserRole };
};

const WorkspaceModel: ReduxSagaModel<
  State,
  {
    saveCurrent: SaveCurrentAction;
    saveInvokeCreateResult: SaveInvokeCreateResultAction;
    saveInvokeRenameResult: SaveInvokeRenameResultAction;
    saveInvokeAddMemberResult: SaveInvokeAddMemberResultAction;
    saveInvokeRemoveMemberResult: SaveInvokeRemoveMemberResultAction;
    saveInvokeUpdateMemberResult: SaveInvokeUpdateMemberResultAction;
    saveInvokeSetHomeDashboardResult: SaveInvokeSetHomeDashboardResultAction;
    saveInvokeCustomizeResult: SaveInvokeCustomizeResultAction;
  }
> = {
  namespace: 'workspaces',
  state: initialState,
  effects: {
    *get(_, { put }) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsDefined(workspaceId, 'BEEM211105120630');
        const workspace = yield* sgcall(() => Workspace.getWorkspaceById(workspaceId));
        logger.debug({ label: `${ns}.get.current`, message: workspace });
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current: workspace } });
      } catch (e) {
        handleUserError(e, `${ns}.get.error`);
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current: null } });
      }
    },
    // The avatar fields are optional: the Create workspace dialog sets them with the name.
    *invokeCreate(
      {
        payload,
      }: {
        payload: {
          name: string;
          avatarColor?: string | null;
          avatarIcon?: string | null;
          avatarEmoji?: string | null;
          avatarInitials?: string | null;
        };
      },
      { put },
    ) {
      try {
        const organizationId = yield* sgselect((s) => s.organization.currentOrganization);
        assertIsString(organizationId, 'BEEM211105120631');
        const currentUser = yield* sgselect((s) => s.user.currentUser);
        assertIsUser(currentUser, 'BEEM220712142308');
        const workspaceId = yield* sgcall(() =>
          Workspace.create(
            {
              input: {
                name: payload.name,
                organizationId,
                seq: 0,
                avatarColor: payload.avatarColor ?? undefined,
                avatarIcon: payload.avatarIcon ?? undefined,
                avatarEmoji: payload.avatarEmoji ?? undefined,
                avatarInitials: payload.avatarInitials ?? undefined,
              },
            },
            { userIds: [currentUser.id] },
          ),
        );
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: true,
              data: workspaceId,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: false,
              error: handleUserError(e, 'app.models.workspaces.invokeCreate.error'),
            },
          },
        });
      }
    },
    *invokeRename({ payload }: { payload: { record: Workspace; name: string } }, { call, put }) {
      try {
        yield call(payload.record.update, { name: payload.name });
        yield put<SaveInvokeRenameResultAction>({
          type: 'saveInvokeRenameResult',
          payload: {
            invokeRenameResult: {
              success: true,
              data: null,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeRenameResultAction>({
          type: 'saveInvokeRenameResult',
          payload: {
            invokeRenameResult: {
              success: false,
              error: handleUserError(e, 'app.models.workspaces.invokeRename.error'),
            },
          },
        });
      }
    },
    // Like `invokeSetHomeDashboard`, `update()` writes the fields back onto the instance.
    *invokeCustomize({ payload }: { payload: InvokeCustomizeEffect['payload'] }, { call, put }) {
      const { record, ...fields } = payload;
      try {
        // null clears a field.
        yield call([record, record.update], fields);
        // The header, the rail and the shared folders read the user's workspace list. It is
        // patched with what was saved rather than refetched: a read racing the write (an earlier
        // refetch landing late, two renames in a row) put the old name back.
        const user = yield* sgselect((s) => s.user);
        if (user.workspaces) {
          user.workspaces
            .filter((ws) => ws.id === record.id)
            .forEach((ws) => Object.assign(ws, fields));
          yield put({
            type: 'user/saveWorkspaces',
            payload: {
              organizations: user.organizations,
              workspaces: [...user.workspaces].sort((x, y) => x.name.localeCompare(y.name)),
            },
          });
        }
        yield put<SaveInvokeCustomizeResultAction>({
          type: 'saveInvokeCustomizeResult',
          payload: { invokeCustomizeResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeCustomizeResultAction>({
          type: 'saveInvokeCustomizeResult',
          payload: {
            invokeCustomizeResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeCustomize.error`),
            },
          },
        });
      }
    },
    // `update()` writes the field back onto the Workspace instance, which is the same object held in
    // `user.workspaces` and `workspaces.current`, so the new value is visible everywhere without a
    // refetch. Selectors reading `homeDashboardId` pick it up on the result put below.
    *invokeSetHomeDashboard(
      { payload }: { payload: InvokeSetHomeDashboardEffect['payload'] },
      { call, put },
    ) {
      try {
        const workspace = yield* sgselect((s) => s.workspaces.current, 'BEEM260922174501');
        // With a context: `update` reads `this.id`, and a bare `call(fn)` runs it with none.
        yield call([workspace, workspace.update], {
          homeDashboardId: payload.dashboardId ?? undefined,
        });
        yield put<SaveInvokeSetHomeDashboardResultAction>({
          type: 'saveInvokeSetHomeDashboardResult',
          payload: { invokeSetHomeDashboardResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeSetHomeDashboardResultAction>({
          type: 'saveInvokeSetHomeDashboardResult',
          payload: {
            invokeSetHomeDashboardResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeSetHomeDashboard.error`),
            },
          },
        });
      }
    },
    *invokeAddMember(
      {
        payload,
      }: {
        payload: {
          memberIds: string[];
          emailList: string[];
          rolesByUserId?: Record<string, UserRole>;
          rolesByEmail?: Record<string, UserRole>;
        };
      },
      { put },
    ) {
      try {
        const workspace = yield* sgselect((s) => s.workspaces.current, 'BEEM220712161101');
        if (payload?.memberIds?.length) {
          yield* sgcall(() => workspace.addMembers(payload.memberIds, payload.rolesByUserId));
        }
        if (payload?.emailList?.length) {
          const createUsersPromises = payload.emailList.map((email: string) =>
            User.createIfNotExist(email, workspace, payload.rolesByEmail?.[email]),
          );

          yield* sgcall(() => Promise.all(createUsersPromises));
        }
        yield put<SaveInvokeAddMemberResultAction>({
          type: 'saveInvokeAddMemberResult',
          payload: {
            invokeAddMemberResult: {
              success: true,
              data: null,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeAddMemberResultAction>({
          type: 'saveInvokeAddMemberResult',
          payload: {
            invokeAddMemberResult: {
              success: false,
              error: handleUserError(e, 'app.models.workspaces.invokeAddMember.error'),
            },
          },
        });
      }
    },
    *invokeRemoveMember({ payload }: { payload: { id: string } }, { call, put }) {
      try {
        const workspace = yield* sgselect((s) => s.workspaces.current);
        assertIsWorkspace(workspace, 'BEEM220712161102');
        yield call(workspace.removeMembers, [payload.id]);
        yield put<SaveInvokeRemoveMemberResultAction>({
          type: 'saveInvokeRemoveMemberResult',
          payload: {
            invokeRemoveMemberResult: {
              success: true,
              data: null,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeRemoveMemberResultAction>({
          type: 'saveInvokeRemoveMemberResult',
          payload: {
            invokeRemoveMemberResult: {
              success: false,
              error: handleUserError(e, 'app.models.workspaces.invokeRemoveMember.error'),
            },
          },
        });
      }
    },
    *invokeUpdateMember({ payload }: { payload: InvokeUpdateMemberEffect['payload'] }, { put }) {
      try {
        const workspace = yield* sgselect((s) => s.workspaces.current);
        assertIsDefined(workspace, 'BEEM220730144208');
        yield* sgcall(() => workspace.updateMemberRole(payload.id, payload.role));
        yield put<SaveInvokeUpdateMemberResultAction>({
          type: 'saveInvokeUpdateMemberResult',
          payload: {
            invokeUpdateMemberResult: {
              success: true,
              data: null,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeUpdateMemberResultAction>({
          type: 'saveInvokeUpdateMemberResult',
          payload: {
            invokeUpdateMemberResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeUpdateMember.error`),
            },
          },
        });
      }
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveCurrent(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCreateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeRenameResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeAddMemberResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeRemoveMemberResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeUpdateMemberResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeSetHomeDashboardResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCustomizeResult(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default WorkspaceModel;
